import * as SelectBase from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import './select-override.css';

export interface Choice {
  label: string;
  key: string;
  value?: string;
}

interface Props<C extends Choice> {
  className?: string;
  choices: C[];
  defaultChoice?: Pick<Choice, 'key'>;
  defaultValue?: string;
  name?: string;
  placeholder?: string;
  renderChoice?(choice: C): React.ReactNode;
  onChange?(choice?: C): void;
  onInvalid?(e: React.InvalidEvent<HTMLSelectElement>): void;
  'aria-label'?: string;
}

export function Select<C extends Choice>(props: Props<C>) {
  const [selectedKey, setSelectedKey] = useState<C['key'] | undefined>(
    props.defaultValue || props.defaultChoice?.key,
  );
  const selected = selectedKey
    ? props.choices.find((c) => c.key === selectedKey)
    : undefined;
  const renderChoice = props.renderChoice || ((choice) => choice.label);

  useEffect(() => {
    if (props.onChange) {
      props.onChange?.(selected);
    }
  }, [selectedKey]);

  return (
    <SelectBase.Root
      name={props.name}
      value={selectedKey}
      onValueChange={(key) =>
        setSelectedKey(props.choices.find((c) => c.key === key)?.key)
      }
    >
      <SelectBase.Trigger
        aria-label={props['aria-label']}
        className={twMerge(
          'bg-transparent',
          'border-seeker-700',
          'border',
          'grid-cols-[1fr_max-content]',
          'grid',
          'group',
          'h-10',
          'items-center',
          'outline-none',
          'px-4',
          'rounded-lg',
          'text-left',
          'text-sm',
          'text-white',
          'transition-colors duration-150',
          'w-full',
          'focus:border-white',
          'data-[state=open]:border-white',
          'hover:border-seeker-500',
          props.className,
        )}
      >
        {selected ? (
          <SelectBase.Value>{renderChoice(selected)}</SelectBase.Value>
        ) : (
          <div className="text-seeker-400">{props.placeholder || 'Select'}</div>
        )}
        <SelectBase.Icon>
          <ChevronDown className="size-4 text-white transition-all group-data-[state=open]:-rotate-180" />
        </SelectBase.Icon>
      </SelectBase.Trigger>
      <SelectBase.Portal>
        <SelectBase.Content
          style={{
            backgroundColor: '#232626',
            border: '1px solid #3b4040',
            borderRadius: '8px',
            maxHeight: '240px',
            overflow: 'auto',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
            zIndex: 50,
            width: 'var(--radix-select-trigger-width)',
            minWidth: 'var(--radix-select-trigger-width)',
          }}
          position="popper"
          sideOffset={8}
          align="start"
        >
          <SelectBase.Viewport style={{ padding: '4px' }}>
            {props.choices.map((choice) => (
              <SelectBase.Item
                style={{
                  color: 'white',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '40px',
                  margin: '2px 0',
                  width: '100%',
                }}
                key={choice.key}
                value={choice.key}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <SelectBase.ItemText>
                  <span style={{ color: 'white' }}>{renderChoice(choice)}</span>
                </SelectBase.ItemText>
              </SelectBase.Item>
            ))}
          </SelectBase.Viewport>
          <SelectBase.Arrow className="fill-neutral-700" />
        </SelectBase.Content>
      </SelectBase.Portal>
    </SelectBase.Root>
  );
}
